import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

import {
  useGetPlayerAllUefaCompetitionQuery,
  useGetPlayerStatusQuery,
  useGetPlayersSeasonCompetitionStatisticQuery,
  useGetPlayersStatisticForCompetitionQuery,
  useGetTeamSquadForCompetitionQuery,
  useGetTeamsByTeamIdQuery,
} from 'api/fspApi';
import { SquadDetailsAccordion } from 'components/SquadDatailsAccordion';
import { SquadDetailsTable } from 'components/SquadDetailsTable';
import useIsDesktop from 'hooks/useIsDesktop';
import { useMatchData } from 'hooks/useMatchData';
import type { PlayerStatistic, Players, SquadGroup } from 'types/ApiResponseInterfaces';
import config from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

const STATS = ['matches_appearance', 'goals', 'yellow_cards', 'red_cards', 'goals_conceded'];

const updateTeamStatistic = (
  playersArr: Array<SquadGroup>,
  seasonStatisticArr: Array<PlayerStatistic>,
  allTimeStatisticArr: Array<PlayerStatistic>,
  playersNotAvailable: Players | undefined,
  allUefaStatisticsArr: Array<PlayerStatistic>,
  isNationalTournament: boolean,
  isWomenCompetition: boolean,
  isSuperCup: boolean,
) => {
  const statisticTranslationEntries = allTimeStatisticArr
    .find((player) => player.statistics)
    ?.statistics.map((s) => {
      return [s.name, s.translations];
    });

  const statisticTranslationObj = statisticTranslationEntries && Object.fromEntries(statisticTranslationEntries);

  return playersArr.reduce((acc: Array<SquadGroup>, el) => {
    const players = [...el.players]
      .map((player) => {
        const playerStatusNextMatch = playersNotAvailable?.players?.find(
          (foundingPlayer) => foundingPlayer.player.id === player.player.id,
        )?.status;

        //find player statistic for all time in allTimeStatisticArr
        const allTimeStats =
          allTimeStatisticArr.find((playerData) => playerData.playerId === player.player.id)?.statistics ?? [];

        const allUefaStats =
          allUefaStatisticsArr.find((playerData) => playerData.playerId === player.player.id)?.statistics ?? [];

        //if a player does not have any stats, we set it to 0 and add translations
        const fullyFilledAllTimeStats = STATS.map((statisticName) => {
          const appropriatePlayer = allTimeStats.find((playerData) => playerData.name === statisticName) ?? {
            name: statisticName,
            value: 0,
            translations: statisticTranslationObj?.[statisticName],
          };

          return appropriatePlayer;
        });

        const fullyFilledAllUefaStats = STATS.map((statisticName) => {
          const appropriatePlayer = allUefaStats.find((playerData) => playerData.name === statisticName) ?? {
            name: statisticName,
            value: 0,
            translations: statisticTranslationObj?.[statisticName],
          };

          return appropriatePlayer;
        });

        //find player statistic for this season in allTimeStatisticArr
        const singleSeasonStats =
          seasonStatisticArr.find((item) => item.playerId === player.player.id)?.statistics ?? [];
        //  const singleSeason = seasonStatisticArr.find(item => item.name === 'matches_appearance');

        //if a player does not have any stats, we set it to 0 and add translations
        const fullyFilledSingleSeasonStats = STATS.map((statisticName) => {
          const appropriatePlayer = singleSeasonStats.find((playerData) => playerData.name === statisticName) ?? {
            name: statisticName,
            value: 0,
            translations: statisticTranslationObj?.[statisticName],
          };

          return appropriatePlayer;
        });

        const isGoalkeeper = player?.player?.fieldPosition === 'GOALKEEPER';

        const updatePlayerStatistic =
          isWomenCompetition || isSuperCup
            ? [
                {
                  name: `matches_appearance/${isGoalkeeper ? 'goals_conceded' : 'goals'}`,
                  subname: 'Competitions',
                  time: 'current_season',
                  value: `${
                    fullyFilledSingleSeasonStats?.find((item) => item.name === 'matches_appearance')?.value ?? 0
                  }/${
                    fullyFilledSingleSeasonStats?.find(
                      (item) => item.name === (isGoalkeeper ? 'goals_conceded' : 'goals'),
                    )?.value ?? 0
                  }`,
                },
                {
                  name: `matches_appearance/${isGoalkeeper ? 'goals_conceded' : 'goals'}`,
                  subname: 'Competitions',
                  time: 'all_time',
                  value: `${fullyFilledAllTimeStats?.find((item) => item.name === 'matches_appearance')?.value ?? 0}/${
                    fullyFilledAllTimeStats?.find((item) => item.name === (isGoalkeeper ? 'goals_conceded' : 'goals'))
                      ?.value ?? 0
                  }`,
                },
              ]
            : [
                {
                  name: `matches_appearance/${isGoalkeeper ? 'goals_conceded' : 'goals'}`,
                  isCurrent: true,
                  value: `${
                    fullyFilledSingleSeasonStats?.find((item) => item.name === 'matches_appearance')?.value ?? 0
                  }/${
                    fullyFilledSingleSeasonStats?.find(
                      (item) => item.name === (isGoalkeeper ? 'goals_conceded' : 'goals'),
                    )?.value ?? 0
                  }`,
                },
                {
                  name: `matches_appearance/${isGoalkeeper ? 'goals_conceded' : 'goals'}`,
                  isAllTime: true,
                  value: `${fullyFilledAllTimeStats?.find((item) => item.name === 'matches_appearance')?.value ?? 0}/${
                    fullyFilledAllTimeStats?.find((item) => item.name === (isGoalkeeper ? 'goals_conceded' : 'goals'))
                      ?.value ?? 0
                  }`,
                },
                {
                  name: `matches_appearance/${isGoalkeeper ? 'goals_conceded' : 'goals'}`,
                  isAllComp: true,
                  value: `${fullyFilledAllUefaStats?.find((item) => item.name === 'matches_appearance')?.value ?? 0}/${
                    fullyFilledAllUefaStats?.find((item) => item.name === (isGoalkeeper ? 'goals_conceded' : 'goals'))
                      ?.value ?? 0
                  }`,
                },
              ];

        const updatedPlayer = {
          ...player,
          player: { ...player.player },
          status: { isSuspended: playerStatusNextMatch?.isSuspended, isBooked: playerStatusNextMatch?.isBooked },
        };

        updatedPlayer.stats = updatePlayerStatistic;

        return updatedPlayer;
      })
      .sort((player, nextPLayer) => {
        if (!player.jerseyNumber || !nextPLayer.jerseyNumber) {
          return 0;
        }

        return player?.jerseyNumber - nextPLayer?.jerseyNumber;
      });

    const filteredPlayers = players.filter((player) => player?.availableForNextMatch !== false);

    if (filteredPlayers.length) {
      acc.push({ ...el, players: filteredPlayers });
    }

    return acc;
  }, []);
};

export const SquadDetailsTab = ({ isPDFPrint = false }) => {
  const { competition, matchData } = useMatchData();

  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const {
    data: homeTeamSquadData,
    isLoading: homeTeamSquadDataLoading,
    isError: homeTeamSquadDataError,
  } = useGetTeamSquadForCompetitionQuery({
    competitionId: matchData.competition.id,
    seasonYear: matchData.seasonYear,
    teamId: matchData?.homeTeam?.id ?? '1',
  });

  const {
    data: awayTeamSquadData,
    isLoading: awayTeamSquadDataLoading,
    isError: awayTeamSquadDataError,
  } = useGetTeamSquadForCompetitionQuery({
    competitionId: matchData.competition.id,
    seasonYear: matchData.seasonYear,
    teamId: matchData?.awayTeam?.id ?? '1',
  });

  const homePlayersIds =
    homeTeamSquadData?.squad.reduce((acc: Array<string>, el) => {
      const arr = el.players.map((player) => player.player.id);

      return [...acc, ...arr];
    }, []) ?? [];

  const awayPlayersIds =
    awayTeamSquadData?.squad.reduce((acc: Array<string>, el) => {
      const arr = el.players.map((player) => player.player.id);

      return [...acc, ...arr];
    }, []) ?? [];

  const isNationalTournament = config && config?.connectedCompetition?.[1]?.includes(matchData.competition.id);

  const {
    data: homeTeamStatisticData = [],
    isLoading: homeTeamStatisticDataLoading,
    isError: homeTeamStatisticDataError,
  } = useGetPlayersStatisticForCompetitionQuery({
    competitionId: matchData.competition.id,
    playerId: homePlayersIds,
    stats: STATS,
  });

  const {
    data: homeTeamStatisticSeasonData = [],
    isLoading: homeTeamStatisticSeasonDataLoading,
    isError: homeTeamStatisticSeasonDataError,
  } = useGetPlayersSeasonCompetitionStatisticQuery({
    competitionId: matchData.competition.id,
    playerId: homePlayersIds,
    stats: STATS,
    seasonYear: matchData.seasonYear,
  });

  const {
    data: homeTeamStatisticAllUefaCompetitionData = [],
    isLoading: homeTeamStatisticAllUefaCompetitionDataLoading,
    isError: homeTeamStatisticAllUefaCompetitionError,
  } = useGetPlayerAllUefaCompetitionQuery({
    competitionId: isNationalTournament ? '3, 17, 19, 2014, 32' : '1, 14, 2019',
    playerId: homePlayersIds,
    stats: STATS,
  });

  const {
    data: awayTeamStatisticData = [],
    isLoading: awayTeamStatisticDataLoading,
    isError: awayTeamStatisticDataError,
  } = useGetPlayersStatisticForCompetitionQuery({
    competitionId: matchData.competition.id,
    playerId: awayPlayersIds,
    stats: STATS,
  });

  const {
    data: awayTeamStatisticSeasonData = [],
    isLoading: awayTeamStatisticSeasonDataLoading,
    isError: awayTeamStatisticSeasonDataError,
  } = useGetPlayersSeasonCompetitionStatisticQuery({
    competitionId: matchData.competition.id,
    playerId: awayPlayersIds,
    stats: STATS,
    seasonYear: matchData.seasonYear,
  });

  const {
    data: awayTeamStatisticAllUefaCompetitionData = [],
    isLoading: awayTeamStatisticAllUefaCompetitionDataLoading,
    isError: awayTeamStatisticAllUefaCompetitionDataError,
  } = useGetPlayerAllUefaCompetitionQuery({
    competitionId: isNationalTournament ? '3, 17, 19, 2014, 32' : '1, 9, 14, 2019',
    playerId: awayPlayersIds,
    stats: STATS,
  });

  let isHomeTeamSquadAvailable = true;
  let isAwayTeamSquadAvailable = true;

  if (
    homeTeamStatisticDataLoading ||
    homeTeamStatisticDataError ||
    homeTeamStatisticSeasonDataLoading ||
    homeTeamStatisticAllUefaCompetitionDataLoading ||
    homeTeamStatisticAllUefaCompetitionError ||
    homeTeamStatisticSeasonDataError
  ) {
    isHomeTeamSquadAvailable = false;
  }

  if (
    awayTeamStatisticDataLoading ||
    awayTeamStatisticDataError ||
    awayTeamStatisticSeasonDataLoading ||
    awayTeamStatisticSeasonDataError ||
    awayTeamStatisticAllUefaCompetitionDataLoading ||
    awayTeamStatisticAllUefaCompetitionDataError
  ) {
    isAwayTeamSquadAvailable = false;
  }

  const { data: homePlayerNotAvailable, isLoading: homePlayerNotAvailableLoading } = useGetPlayerStatusQuery({
    seasonYear: matchData.seasonYear,
    matchId: matchData.id,
    teamId: matchData.homeTeam?.id || '',
    status: 'ALL',
  });

  const { data: awayPlayerNotAvailable, isLoading: awayPlayerNotAvailableLoading } = useGetPlayerStatusQuery({
    seasonYear: matchData.seasonYear,
    matchId: matchData.id,
    teamId: matchData.awayTeam?.id || '',
    status: 'ALL',
  });

  const home =
    homeTeamSquadData !== undefined
      ? updateTeamStatistic(
          homeTeamSquadData?.squad,
          homeTeamStatisticSeasonData,
          homeTeamStatisticData,
          homePlayerNotAvailable,
          homeTeamStatisticAllUefaCompetitionData,
          isNationalTournament,
          matchData.competition.id === '18' || matchData.competition.id === '28',
          matchData.competition.id === '9',
        )
      : [];

  const away =
    awayTeamSquadData !== undefined
      ? updateTeamStatistic(
          awayTeamSquadData?.squad,
          awayTeamStatisticSeasonData,
          awayTeamStatisticData,
          awayPlayerNotAvailable,
          awayTeamStatisticAllUefaCompetitionData,
          isNationalTournament,
          matchData.competition.id === '18' || matchData.competition.id === '28',
          matchData.competition.id === '9',
        )
      : [];

  const teamsSquads = [...(homeTeamSquadData?.squad ?? []), ...(awayTeamSquadData?.squad ?? [])];
  const teamsIds = teamsSquads
    .flatMap((playerGroup: SquadGroup) => playerGroup?.players.map((player) => player?.player?.clubId))
    .filter(Boolean);

  const { data: teamsNames = [] } = useGetTeamsByTeamIdQuery(
    {
      teamIds: teamsIds,
    },
    {
      skip: !teamsIds.length,
    },
  );

  if (
    homeTeamSquadDataLoading ||
    homeTeamSquadDataError ||
    awayTeamSquadDataLoading ||
    awayTeamSquadDataError ||
    awayPlayerNotAvailableLoading ||
    homePlayerNotAvailableLoading
  ) {
    return <div />;
  }

  return (
    <div className="pk-container pk-pt--l pk-pt-md--xl2">
      {isDesktop || (isDesktop && isPDFPrint) ? (
        <>
          <div className="pk-col pk-col--span-12">
            <div className="pk-font-size--xl2 pk-font-size-md--xl3 pk-line-height--sm pk-font-style--normal pk-font--comp-bold pk-text--text-01 pk-mb--xs2 pr-font-size--14">
              {translateEntity(matchData?.homeTeam)?.displayName ?? t('home_team')}
            </div>
            {isNationalTournament ? <p className="pk-mb--xl">{t('squad_lists_updated_morning')}</p> : ''}
            {isHomeTeamSquadAvailable ? (
              home.map((el, i, arr) => (
                <SquadDetailsTable
                  competition={competition}
                  elementIndex={{ index: i, length: arr.length }}
                  isPDFPrint={isPDFPrint}
                  key={i}
                  teams={teamsNames}
                  matchData={matchData}
                  squad={el}
                />
              ))
            ) : (
              <div>{t('squad_details_is_not_available_at_the_moment')}</div>
            )}
          </div>
          <div className={clsx({ 'pk-col pk-col--span-12': true, 'pr-page-break--before': isPDFPrint })}>
            <div className="pk-font-size--xl2 pk-font-size-md--xl3 pk-line-height--sm pk-font-style--normal pk-font--comp-bold pk-text--text-01 pk-mb--xs2 pr-font-size--14">
              {translateEntity(matchData?.awayTeam)?.displayName ?? t('away_team')}
            </div>
            {isNationalTournament ? <p className="pk-mb--xl">{t('squad_lists_updated_morning')}</p> : ''}
            {isAwayTeamSquadAvailable ? (
              away.map((el, i, arr) => (
                <SquadDetailsTable
                  competition={competition}
                  elementIndex={{ index: i, length: arr.length }}
                  isPDFPrint={isPDFPrint}
                  key={i}
                  teams={teamsNames}
                  matchData={matchData}
                  squad={el}
                />
              ))
            ) : (
              <div>{t('squad_details_is_not_available_at_the_moment')}</div>
            )}
          </div>
          {(isAwayTeamSquadAvailable || isHomeTeamSquadAvailable) &&
            matchData.competition.id !== '18' &&
            matchData.competition.id !== '28' && (
              <div className="pk-col pk-col--span-12 pk-mb--l pk-font--base-regular">
                *{isNationalTournament ? t('national_team_description') : t('uefa_team_description')}
              </div>
            )}
        </>
      ) : (
        <div className="pk-col pk-col--span-12">
          <>
            <div className="pk-font-size--xl2 pk-font-size-md--xl3 pk-line-height--sm pk-font-style--normal pk-font--comp-bold pk-my--0 pk-text--text-01">
              {translateEntity(matchData?.homeTeam)?.displayName ?? t('home_team')}
            </div>
            {isHomeTeamSquadAvailable ? (
              home.map((el, i) => (
                <SquadDetailsAccordion
                  teams={teamsNames}
                  squad={el}
                  key={i * Math.random()}
                  competition={competition}
                />
              ))
            ) : (
              <div>{t('squad_details_is_not_available_at_the_moment')}</div>
            )}
          </>
          <>
            <div className="pk-font-size--xl2 pk-font-size-md--xl3 pk-line-height--sm pk-font-style--normal pk-font--comp-bold pk-my--0 pk-text--text-01">
              {translateEntity(matchData?.awayTeam)?.displayName ?? t('away_team')}
            </div>
            {isAwayTeamSquadAvailable ? (
              away.map((el, i) => (
                <SquadDetailsAccordion
                  teams={teamsNames}
                  squad={el}
                  key={i * Math.random()}
                  competition={competition}
                />
              ))
            ) : (
              <div className="pk-col pk-col--span-12">{t('squad_details_is_not_available_at_the_moment')}</div>
            )}
          </>
          {(isAwayTeamSquadAvailable || isHomeTeamSquadAvailable) &&
            matchData.competition.id !== '18' &&
            matchData.competition.id !== '28' && (
              <div className="pk-col pk-col--span-12 pk-my--l pk-my-pk-m-sm--0 pk-font--base-regular">
                *{isNationalTournament ? t('national_team_description') : t('uefa_team_description')}
              </div>
            )}
        </div>
      )}
    </div>
  );
};
